import offlineDb from "../db/appDb";
import { createOpname, updateOpname, uploadOpnamePhoto } from "../api/opnames";
import {
  createOpnameDraftId,
  deletePendingOpnamePhoto,
  deleteDraftOpname,
  getPendingOpnamePhoto,
  getCachedOpnames,
  setCachedOpnameDetail,
  setCachedOpnames,
} from "./opnameOfflineService";

const ACTIVE_QUEUE_STATUSES = new Set(["pending", "retry_waiting", "syncing"]);
const RETRYABLE_STATUSES = new Set(["pending", "retry_waiting"]);
const TEMP_ID_PREFIX = "local:";
const activeSyncRunsByScope = new Map();

function nowIso() {
  return new Date().toISOString();
}

function randomId() {
  return Math.random().toString(36).slice(2, 10);
}

function toSafeScopeKey(scopeKey) {
  return String(scopeKey || "").trim();
}

function toSafeEntityId(entityId) {
  return String(entityId || "").trim();
}

function toSafeType(type) {
  return String(type || "").trim();
}

function buildQueueTempId() {
  return `queue:${Date.now()}:${randomId()}`;
}

function clonePayload(payload) {
  return payload ? JSON.parse(JSON.stringify(payload)) : {};
}

function buildStablePhotoUrl(fileId, fallbackUrl = "") {
  const normalizedFileId = String(fileId || "").trim();
  if (normalizedFileId) {
    return `/api/files/${normalizedFileId}/view`;
  }
  const rawUrl = String(fallbackUrl || "").trim();
  if (!rawUrl) return "";
  return rawUrl.split("#")[0].split("?")[0].trim();
}

function normalizeStoredPhotoRef(photo) {
  const fileId = String(photo?.fileId || "").trim();
  return {
    fileId,
    url: buildStablePhotoUrl(fileId, photo?.url || photo?.fileUrl || photo?.viewUrl || ""),
    caption: String(photo?.caption || "").trim(),
  };
}

function sanitizePayloadForOfflineStorage(payload) {
  const nextPayload = clonePayload(payload);
  if (!String(nextPayload?.sourceRequestId || "").trim()) {
    nextPayload.sourceRequestId = createOpnameClientMutationId();
  }
  nextPayload.items = Array.isArray(nextPayload.items)
    ? nextPayload.items.map((item) => ({
        ...item,
        photos: Array.isArray(item?.photos)
          ? item.photos
              .map((photo) => {
                const normalized = normalizeStoredPhotoRef(photo);
                const localPhotoId = String(photo?.localPhotoId || "").trim();
                return {
                  ...normalized,
                  ...(localPhotoId ? { localPhotoId } : {}),
                  ...(photo?.pendingUpload === true ? { pendingUpload: true } : {}),
                  ...(photo?.fileName ? { fileName: String(photo.fileName).trim() } : {}),
                };
              })
              .filter((photo) => photo.fileId || photo.url || photo.localPhotoId)
          : [],
      }))
    : [];
  return nextPayload;
}

function normalizePhotoCleanupIds(value) {
  return Array.isArray(value)
    ? Array.from(new Set(value.map((item) => String(item || "").trim()).filter(Boolean)))
    : [];
}

export function createLocalOpnameId() {
  return `${TEMP_ID_PREFIX}${Date.now()}:${randomId()}`;
}

export function isLocalOpnameId(value) {
  return String(value || "").trim().startsWith(TEMP_ID_PREFIX);
}

function isRetryableError(error) {
  if (!error) return true;
  const status = Number(error?.status || error?.response?.status || 0);
  if (!status) return true;
  return status === 408 || status === 425 || status === 429 || status >= 500;
}

function isValidationOrConflictError(error) {
  const status = Number(error?.status || error?.response?.status || 0);
  return status === 400 || status === 403 || status === 404 || status === 409 || status === 422;
}

function createBackoffDelayMs(retryCount) {
  const attempt = Math.max(1, Number(retryCount || 0) + 1);
  return Math.min(300000, 2000 * 2 ** (attempt - 1));
}

function createMissingPendingPhotoError(photoId) {
  const error = new Error("Lokale opnamefoto ontbreekt en kan niet worden gesynchroniseerd");
  error.status = 422;
  error.code = "OPNAME_PENDING_PHOTO_MISSING";
  error.photoId = String(photoId || "").trim();
  return error;
}

export function payloadHasPendingOpnamePhotos(payload) {
  return Boolean(
    payload?.items?.some((item) =>
      Array.isArray(item?.photos) &&
      item.photos.some((photo) => String(photo?.localPhotoId || "").trim())
    )
  );
}

export async function resolvePendingOpnamePhotoUploads({ scopeKey, entityId, payload }) {
  if (!payloadHasPendingOpnamePhotos(payload)) {
    return {
      payload: sanitizePayloadForOfflineStorage(payload),
      uploadedLocalPhotoIds: [],
    };
  }

  const nextPayload = sanitizePayloadForOfflineStorage(payload);
  nextPayload.items = Array.isArray(nextPayload.items) ? nextPayload.items : [];
  const uploadedLocalPhotoIds = [];

  for (const item of nextPayload.items) {
    const nextPhotos = [];
    for (const photo of Array.isArray(item?.photos) ? item.photos : []) {
      const localPhotoId = String(photo?.localPhotoId || "").trim();
      if (!localPhotoId) {
        if (String(photo?.url || "").trim()) {
          nextPhotos.push({
            url: String(photo.url || "").trim(),
            caption: String(photo.caption || "").trim(),
          });
        }
        continue;
      }

      const pendingPhoto = await getPendingOpnamePhoto({ scopeKey, photoId: localPhotoId });
      if (!pendingPhoto?.blob) {
        throw createMissingPendingPhotoError(localPhotoId);
      }

      const uploaded = await uploadOpnamePhoto(
        pendingPhoto.blob,
        pendingPhoto.fileName || `opname-${entityId || "draft"}-${localPhotoId}.jpg`
      );

      uploadedLocalPhotoIds.push(localPhotoId);
      nextPhotos.push(
        normalizeStoredPhotoRef({
          fileId: String(uploaded?.fileId || "").trim(),
          url: String(uploaded?.url || uploaded?.fileUrl || uploaded?.viewUrl || "").trim(),
          caption: String(photo?.caption || "").trim(),
        })
      );
    }
    item.photos = nextPhotos.filter((photo) => String(photo?.url || photo?.fileId || "").trim());
  }

  return {
    payload: nextPayload,
    uploadedLocalPhotoIds,
  };
}

export async function cleanupResolvedPendingOpnamePhotos({ scopeKey, uploadedLocalPhotoIds }) {
  const safeScopeKey = toSafeScopeKey(scopeKey);
  const ids = normalizePhotoCleanupIds(uploadedLocalPhotoIds);
  if (!safeScopeKey || !ids.length) return;
  await Promise.all(ids.map((photoId) => deletePendingOpnamePhoto({ scopeKey: safeScopeKey, photoId })));
}

async function putQueueItem(record) {
  const nextRecord = {
    retryCount: 0,
    status: "pending",
    createdAt: nowIso(),
    queuedAt: nowIso(),
    updatedAt: nowIso(),
    ...record,
  };
  await offlineDb.syncQueue.put(nextRecord);
  return nextRecord;
}

export async function listQueuedOpnameMutations({ scopeKey, includeDone = false } = {}) {
  const safeScopeKey = toSafeScopeKey(scopeKey);
  if (!safeScopeKey) return [];
  const rows = await offlineDb.syncQueue.where("scopeKey").equals(safeScopeKey).sortBy("queuedAt");
  return rows.filter((item) => {
    if (String(item?.entityType || "") !== "opname") return false;
    if (includeDone) return true;
    return String(item?.status || "") !== "done";
  });
}

export async function getQueueItemsForEntity({ scopeKey, entityId }) {
  const safeScopeKey = toSafeScopeKey(scopeKey);
  const safeEntityId = toSafeEntityId(entityId);
  if (!safeScopeKey || !safeEntityId) return [];
  const rows = await offlineDb.syncQueue
    .where("[scopeKey+entityId]")
    .equals([safeScopeKey, safeEntityId])
    .toArray();
  return rows.filter((item) => String(item?.entityType || "") === "opname");
}

export async function clearQueuedOpnameMutations({ scopeKey, entityId }) {
  const rows = await getQueueItemsForEntity({ scopeKey, entityId });
  await Promise.all(rows.map((item) => offlineDb.syncQueue.delete(item.tempId)));
}

export async function queueOpnameMutation({
  scopeKey,
  tenantId,
  userId,
  action,
  entityId,
  payload,
  meta = {},
}) {
  const safeScopeKey = toSafeScopeKey(scopeKey);
  const safeEntityId = toSafeEntityId(entityId);
  const type = action === "create" ? "opname.create" : "opname.update";
  if (!safeScopeKey || !safeEntityId) {
    throw new Error("Queue item mist scopeKey of entityId");
  }

  const existingItems = await getQueueItemsForEntity({ scopeKey: safeScopeKey, entityId: safeEntityId });
  const existing = existingItems.find(
    (item) => toSafeType(item?.type) === type && ACTIVE_QUEUE_STATUSES.has(String(item?.status || ""))
  );

  const normalizedPayload = sanitizePayloadForOfflineStorage(payload);
  const baseRecord = {
    tempId: existing?.tempId || buildQueueTempId(),
    type,
    entityType: "opname",
    entityId: safeEntityId,
    scopeKey: safeScopeKey,
    tenantId: String(tenantId || "").trim(),
    userId: String(userId || "").trim(),
    payload: normalizedPayload,
    meta,
    retryCount: Number(existing?.retryCount || 0),
    status: existing ? String(existing.status || "pending") : "pending",
    createdAt: existing?.createdAt || nowIso(),
    queuedAt: existing?.queuedAt || nowIso(),
    updatedAt: nowIso(),
    nextRetryAt: "",
    lastError: null,
  };

  await putQueueItem(baseRecord);
  return baseRecord;
}

function mergeListItem(list, savedItem, previousEntityId = "") {
  const safePreviousId = toSafeEntityId(previousEntityId);
  const safeSavedId = toSafeEntityId(savedItem?._id);
  const nextList = Array.isArray(list) ? [...list] : [];
  const nextIndex = nextList.findIndex((item) => {
    const itemId = toSafeEntityId(item?._id);
    return itemId === safeSavedId || (safePreviousId && itemId === safePreviousId);
  });
  if (nextIndex >= 0) {
    nextList[nextIndex] = {
      ...nextList[nextIndex],
      ...savedItem,
      _offlineStatus: "",
    };
    return nextList;
  }
  return [{ ...savedItem, _offlineStatus: "" }, ...nextList];
}

export async function upsertOfflineOpnameSnapshot({
  scopeKey,
  entityId,
  payload,
  offlineStatus = "draft",
}) {
  const safeScopeKey = toSafeScopeKey(scopeKey);
  const safeEntityId = toSafeEntityId(entityId);
  if (!safeScopeKey || !safeEntityId) return null;

  const cachedList = await getCachedOpnames({ scopeKey: safeScopeKey, cacheKey: "mine" });
  const currentList = Array.isArray(cachedList?.payload) ? cachedList.payload : [];
  const nextList = mergeListItem(
    currentList,
    {
      ...sanitizePayloadForOfflineStorage(payload),
      _id: safeEntityId,
      _offlineStatus: offlineStatus,
      updatedAt: payload?.updatedAt || nowIso(),
    },
    safeEntityId
  );

  await Promise.all([
    setCachedOpnames({ scopeKey: safeScopeKey, cacheKey: "mine", payload: nextList }),
    setCachedOpnameDetail({ scopeKey: safeScopeKey, opnameId: safeEntityId, payload: payload || null }),
  ]);

  return nextList;
}

async function replaceOpnameSnapshotAfterSync({ scopeKey, previousEntityId, savedItem }) {
  const safeScopeKey = toSafeScopeKey(scopeKey);
  if (!safeScopeKey || !savedItem?._id) return;

  const cachedList = await getCachedOpnames({ scopeKey: safeScopeKey, cacheKey: "mine" });
  const currentList = Array.isArray(cachedList?.payload) ? cachedList.payload : [];
  const nextList = mergeListItem(currentList, savedItem, previousEntityId);

  await Promise.all([
    setCachedOpnames({ scopeKey: safeScopeKey, cacheKey: "mine", payload: nextList }),
    setCachedOpnameDetail({
      scopeKey: safeScopeKey,
      opnameId: String(savedItem._id),
      payload: savedItem,
    }),
    deleteDraftOpname({
      scopeKey: safeScopeKey,
      draftId: createOpnameDraftId(previousEntityId),
    }),
    deleteDraftOpname({
      scopeKey: safeScopeKey,
      draftId: createOpnameDraftId(String(savedItem._id)),
    }),
  ]);

  if (previousEntityId && previousEntityId !== String(savedItem._id)) {
    await offlineDb.opnameDetails.delete([safeScopeKey, previousEntityId]);
  }
}

async function markQueueItem(tempId, patch) {
  const current = await offlineDb.syncQueue.get(tempId);
  if (!current) return null;
  const next = {
    ...current,
    ...patch,
    updatedAt: nowIso(),
  };
  await offlineDb.syncQueue.put(next);
  return next;
}

async function processQueueItem(item, callbacks) {
  await markQueueItem(item.tempId, { status: "syncing" });

  try {
    const existingCleanupIds = normalizePhotoCleanupIds(item?.meta?.pendingPhotoCleanupIds);
    const {
      payload: resolvedPayload,
      uploadedLocalPhotoIds,
    } = await resolvePendingOpnamePhotoUploads({
      scopeKey: item.scopeKey,
      entityId: item.entityId,
      payload: item.payload || {},
    });
    const cleanupIds = normalizePhotoCleanupIds([...existingCleanupIds, ...uploadedLocalPhotoIds]);

    const persistedQueueItem = await markQueueItem(item.tempId, {
      payload: resolvedPayload,
      meta: {
        ...(item?.meta || {}),
        pendingPhotoCleanupIds: cleanupIds,
      },
    });
    const activeItem = persistedQueueItem || item;

    let savedItem = null;
  if (activeItem.type === "opname.create") {
      savedItem = await createOpname(resolvedPayload);
    } else if (activeItem.type === "opname.update") {
      savedItem = await updateOpname(activeItem.entityId, resolvedPayload);
    } else {
      throw new Error(`Onbekend queue-item type: ${activeItem.type}`);
    }

    await replaceOpnameSnapshotAfterSync({
      scopeKey: activeItem.scopeKey,
      previousEntityId: activeItem.entityId,
      savedItem,
    });

    await cleanupResolvedPendingOpnamePhotos({
      scopeKey: activeItem.scopeKey,
      uploadedLocalPhotoIds: cleanupIds,
    });

    const doneItem = await markQueueItem(item.tempId, {
      status: "done",
      syncedAt: nowIso(),
      lastError: null,
      nextRetryAt: "",
      meta: {
        ...(activeItem?.meta || {}),
        pendingPhotoCleanupIds: [],
      },
    });

    if (callbacks?.onItemSynced) {
      await callbacks.onItemSynced({
        queueItem: doneItem,
        savedItem,
        previousEntityId: item.entityId,
      });
    }
    return { status: "done", queueItem: doneItem, savedItem };
  } catch (error) {
    const retryCount = Number(item?.retryCount || 0) + 1;
    if (isValidationOrConflictError(error) && !isRetryableError(error)) {
      const attentionItem = await markQueueItem(item.tempId, {
        status: "needs_attention",
        retryCount,
        lastError: {
          message: String(error?.message || "Synchronisatie mislukt"),
          status: Number(error?.status || error?.response?.status || 0) || undefined,
        },
      });
      if (callbacks?.onItemFailed) {
        await callbacks.onItemFailed({ queueItem: attentionItem, error });
      }
      return { status: "needs_attention", queueItem: attentionItem, error };
    }

    const delayMs = createBackoffDelayMs(retryCount);
    const retryItem = await markQueueItem(item.tempId, {
      status: "retry_waiting",
      retryCount,
      nextRetryAt: new Date(Date.now() + delayMs).toISOString(),
      lastError: {
        message: String(error?.message || "Synchronisatie mislukt"),
        status: Number(error?.status || error?.response?.status || 0) || undefined,
      },
    });
    if (callbacks?.onItemFailed) {
      await callbacks.onItemFailed({ queueItem: retryItem, error });
    }
    return { status: "retry_waiting", queueItem: retryItem, error };
  }
}

export async function runQueuedOpnameSync({ scopeKey, callbacks } = {}) {
  const safeScopeKey = toSafeScopeKey(scopeKey);
  if (!safeScopeKey) {
    return { processed: 0, completed: 0, failed: 0, needsAttention: 0 };
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { processed: 0, completed: 0, failed: 0, needsAttention: 0, skipped: "offline" };
  }

  const existingRun = activeSyncRunsByScope.get(safeScopeKey);
  if (existingRun) return existingRun;

  const activeRun = (async () => {
    const allRows = await listQueuedOpnameMutations({ scopeKey: safeScopeKey });
    const now = Date.now();
    const queue = allRows.filter((item) => {
      const status = String(item?.status || "");
      if (!RETRYABLE_STATUSES.has(status)) return false;
      const nextRetryAt = item?.nextRetryAt ? new Date(item.nextRetryAt).getTime() : 0;
      return !nextRetryAt || Number.isNaN(nextRetryAt) || nextRetryAt <= now;
    });

    const summary = {
      processed: 0,
      completed: 0,
      failed: 0,
      needsAttention: 0,
    };

    for (const item of queue) {
      summary.processed += 1;
      const result = await processQueueItem(item, callbacks);
      if (result.status === "done") {
        summary.completed += 1;
      } else if (result.status === "needs_attention") {
        summary.needsAttention += 1;
      } else {
        summary.failed += 1;
      }
    }

    return summary;
  })().finally(() => {
    activeSyncRunsByScope.delete(safeScopeKey);
  });

  activeSyncRunsByScope.set(safeScopeKey, activeRun);
  return activeRun;
}

export function createOpnameClientMutationId() {
  return `opname-create:${Date.now()}:${randomId()}`;
}
